import { useId, useState, type ReactNode } from "react";

interface TooltipProps {
    /** The hover explainer content. */
    content: ReactNode;
    children: ReactNode;
    /** Optional max-width class for the bubble. */
    className?: string;
}

/**
 * Lightweight hover/focus tooltip. No dependency — pure CSS positioning.
 * Accessible: trigger is focusable, the bubble appears on focus too, and the trigger references
 * the bubble via aria-describedby so screen readers announce the explainer. (Copilot review.)
 */
export const Tooltip: React.FC<TooltipProps> = ({ content, children, className = "" }) => {
    const [open, setOpen] = useState(false);
    const tooltipId = useId();

    if (content == null || content === "") return <>{children}</>;

    return (
        <span
            className="relative inline-flex items-center"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            tabIndex={0}
            aria-describedby={open ? tooltipId : undefined}
        >
            {children}
            {open && (
                <span
                    id={tooltipId}
                    role="tooltip"
                    className={`absolute bottom-full left-1/2 z-50 mb-1.5 w-64 -translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-xs leading-relaxed text-gray-100 shadow-lg ${className}`}
                >
                    {content}
                    <span className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                </span>
            )}
        </span>
    );
};

export default Tooltip;
